import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AGENT_STAGES } from "../../../src/sandbox/agent-stages.ts";
import {
  SPEND_DIMENSIONS,
  type SpendBreakdown,
  type SpendDay,
  type SpendDimension,
  type SpendRange,
  type SpendSummary,
  type SpendTopRequests,
} from "../../../src/admin/spend.ts";
import type { BillCheck } from "../../../src/admin/bill-check.ts";
import { fetchJson } from "../lib/fetchJson.ts";
import { formatSpend, formatTokens } from "../lib/spend.ts";
import { Refusal } from "./bits.tsx";
import { PricePanel } from "./PricePanel.tsx";
import {
  percent,
  requestName,
  spendRequestPath,
  stageColor,
  totalTokens,
  UsageCells,
  UsageHeads,
} from "./spend-bits.tsx";

// The Spend screen: what the platform spent on models over a chosen range.
// Stat tiles, the daily trend by stage, breakdowns by one dimension, the
// most expensive Requests, the bill check against OpenRouter and the price
// table. Every figure comes from the spend ledger's routes; the screen names
// a Request by its title and shows numbers, never what a Request says.
// See ADR-0051 §6.

const DAY_MS = 86_400_000;
const TOP_SHOWN = 10;

// How many Requests the cost-per-Request tile averages over: the route's
// ceiling.
const TOP_READ = 100;

type Preset = "7" | "30" | "custom";

const PRESETS: readonly { id: Preset; label: string }[] = [
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "custom", label: "Custom" },
];

const DIMENSION_LABELS: Record<SpendDimension, string> = {
  organisation: "Organisation",
  project: "Project",
  stage: "Stage",
  runtime: "Runtime",
  model: "Model",
};

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

function todayMs(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

// The last `days` UTC days ending today, both ends inclusive.
function lastDays(days: number): SpendRange {
  const today = todayMs();
  return { from: isoDay(today - (days - 1) * DAY_MS), to: isoDay(today) };
}

const rangeQuery = (r: SpendRange): string => `from=${r.from}&to=${r.to}`;

/** The Request id a Spend path opens, as in `/spend/requests/:id`; null for the Spend screen itself. */
export function spendRequestIdOf(pathname: string): string | null {
  const m = /^\/spend\/requests\/([^/]+)\/?$/.exec(pathname);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

// One read of a route: the body once it lands, or the refusal. Re-reads
// whenever the url or the reload key changes, and drops an answer that a
// newer read has overtaken.
function useRead<T>(url: string, reload: number): { data: T | null; error: string | null } {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  useEffect(() => {
    let live = true;
    fetchJson<T>(url)
      .then((data) => live && setState({ data, error: null }))
      .catch((e: Error) => live && setState({ data: null, error: e.message }));
    return () => {
      live = false;
    };
  }, [url, reload]);
  // A range change shows the old figures until the new ones land, rather
  // than flashing every panel back to Loading.
  return state;
}

/**
 * The Spend screen: the range picker, the stat tiles, the daily trend, the
 * breakdown, the top Requests, the bill check and the price table. Every
 * panel re-reads when the range changes, and the totals re-read after the
 * price table prices unpriced rows. Rendered by the admin shell at `/spend`.
 */
export function SpendPage(): JSX.Element {
  const [preset, setPreset] = useState<Preset>("30");
  const [range, setRange] = useState<SpendRange>(() => lastDays(30));
  const [custom, setCustom] = useState<SpendRange>(range);
  const [reload, setReload] = useState(0);

  function pick(p: Preset): void {
    setPreset(p);
    if (p === "custom") setCustom(range);
    else setRange(lastDays(Number(p)));
  }

  // A custom range applies once both ends are dates and in order; until
  // then the screen keeps the last range it read.
  function editCustom(next: SpendRange): void {
    setCustom(next);
    if (/^\d{4}-\d{2}-\d{2}$/.test(next.from) && /^\d{4}-\d{2}-\d{2}$/.test(next.to) && next.from <= next.to) {
      setRange(next);
    }
  }

  const q = rangeQuery(range);
  const summary = useRead<{ summary: SpendSummary }>(`/api/spend/summary?${q}`, reload);
  const top = useRead<{ topRequests: SpendTopRequests }>(`/api/spend/requests?${q}&limit=${TOP_READ}`, reload);

  return (
    <div className="spend-page">
      <p className="lede">
        What the platform spent on models, from the spend ledger: every call a Run, a refinement
        or a pull request description made, priced as it landed. Requests appear by title and
        numbers only.
      </p>

      <div className="spend-range">
        <div className="seg" role="radiogroup" aria-label="Range">
          {PRESETS.map((p) => (
            <label key={p.id}>
              <input type="radio" name="spend-range" checked={preset === p.id} onChange={() => pick(p.id)} />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
        {preset === "custom" ? (
          <div className="spend-custom">
            <label htmlFor="spend-from">From</label>
            <input
              id="spend-from"
              type="date"
              value={custom.from}
              max={custom.to || undefined}
              onChange={(e) => editCustom({ ...custom, from: e.target.value })}
            />
            <label htmlFor="spend-to">To</label>
            <input
              id="spend-to"
              type="date"
              value={custom.to}
              min={custom.from || undefined}
              onChange={(e) => editCustom({ ...custom, to: e.target.value })}
            />
          </div>
        ) : null}
        <span className="meta">
          {range.from} → {range.to} · UTC
        </span>
      </div>

      <Refusal message={summary.error ?? top.error} />

      <StatTiles summary={summary.data?.summary ?? null} top={top.data?.topRequests ?? null} />

      <TrendPanel summary={summary.data?.summary ?? null} range={range} />

      <div className="spend-grid-2">
        <BreakdownPanel range={range} reload={reload} />
        <TopRequestsPanel top={top.data?.topRequests ?? null} />
      </div>

      <BillCheckPanel range={range} />

      <PricePanel onPriced={() => setReload((n) => n + 1)} />
    </div>
  );
}

// A coverage share as a whole percentage that never rounds a few rows
// away: a sliver reads `<1%`, nearly all `>99%`.
function coverage(share: number): string {
  if (share > 0 && share < 0.01) return "<1%";
  if (share > 0.99 && share < 1) return ">99%";
  return percent(share);
}

function StatTiles({ summary, top }: { summary: SpendSummary | null; top: SpendTopRequests | null }): JSX.Element {
  const loading = "…";
  const t = summary?.totals;
  const requests = top?.rows ?? [];
  const requestCost = requests.reduce((n, r) => n + r.costMicros, 0);
  const s = summary?.sources;
  const calls = s ? s.reported.calls + s.priced.calls + s.unpriced.calls : 0;
  const share = (n: number) => (calls === 0 ? 0 : n / calls);
  return (
    <div className="stats spend-stats">
      <div className="stat" data-testid="stat-cost">
        <div className="k">Total cost</div>
        <div className="v">{t ? formatSpend(t.costMicros) : loading}</div>
        {t ? (
          <div className="sub">
            {t.calls} calls{t.unpricedCalls > 0 ? ` · ${t.unpricedCalls} unpriced, not counted` : ""}
          </div>
        ) : null}
      </div>
      <div className="stat" data-testid="stat-tokens">
        <div className="k">Total tokens</div>
        <div className="v">{t ? formatTokens(totalTokens(t)) : loading}</div>
        {t ? (
          <div className="sub">
            in {formatTokens(t.inputTokens)} · out {formatTokens(t.outputTokens)} · cache {percent(t.cacheHitRatio)}
          </div>
        ) : null}
      </div>
      <div className="stat" data-testid="stat-per-request">
        <div className="k">Cost per Request</div>
        <div className="v">{top ? (requests.length === 0 ? "—" : formatSpend(Math.round(requestCost / requests.length))) : loading}</div>
        {top ? (
          <div className="sub">
            {requests.length === TOP_READ
              ? `over the ${TOP_READ} most expensive Requests`
              : `${requests.length} ${requests.length === 1 ? "Request" : "Requests"}`}
          </div>
        ) : null}
      </div>
      <div className="stat" data-testid="stat-coverage">
        <div className="k">Coverage</div>
        <div className="v">{s ? (calls === 0 ? "—" : `${coverage(share(s.reported.calls + s.priced.calls))} costed`) : loading}</div>
        {s && calls > 0 ? (
          <>
            <div className="coverage-bar" aria-hidden="true">
              {(["reported", "priced", "unpriced"] as const).map((k) =>
                s[k].calls > 0 ? <i key={k} className={k} style={{ width: `${share(s[k].calls) * 100}%` }} /> : null,
              )}
            </div>
            <div className="coverage-key">
              {(["reported", "priced", "unpriced"] as const).map((k) => (
                <span key={k}>
                  <i className={k} aria-hidden="true" />
                  {k} {coverage(share(s[k].calls))}
                </span>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// The chart's own box: its width follows the panel, so the labels keep
// their size at any width.
function useWidth(fallback: number): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const w = Math.round(entry.contentRect.width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// A round ceiling for the y axis: 1, 2 or 5 times a power of ten.
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 5, 10].map((m) => m * p).find((n) => n >= v)!;
}

function daysOf(range: SpendRange): string[] {
  const out: string[] = [];
  const end = Date.parse(`${range.to}T00:00:00Z`);
  for (let t = Date.parse(`${range.from}T00:00:00Z`); t <= end && out.length < 400; t += DAY_MS) out.push(isoDay(t));
  return out;
}

function TrendPanel({ summary, range }: { summary: SpendSummary | null; range: SpendRange }): JSX.Element {
  const [ref, width] = useWidth(720);
  let body: JSX.Element;
  if (summary === null) {
    body = <p aria-live="polite">Loading…</p>;
  } else if (summary.totals.calls === 0) {
    body = <p className="empty">No model usage was reported in this range.</p>;
  } else {
    body = <TrendChart daily={summary.daily} days={daysOf(summary.range ?? range)} width={width} />;
  }
  return (
    <section className="panel spend-trend" aria-label="Daily trend">
      <div className="panel-head">
        <h2>Daily trend</h2>
        <span className="meta">cost per UTC day, stacked by stage</span>
      </div>
      <div className="panel-body" ref={ref}>
        {body}
      </div>
    </section>
  );
}

function TrendChart({ daily, days, width }: { daily: SpendDay[]; days: string[]; width: number }): JSX.Element {
  const byDay = new Map<string, SpendDay[]>();
  for (const d of daily) byDay.set(d.day, [...(byDay.get(d.day) ?? []), d]);
  const order = (stage: string) => {
    const i = (AGENT_STAGES as readonly string[]).indexOf(stage);
    return i === -1 ? AGENT_STAGES.length : i;
  };
  const stages = [...new Set(daily.map((d) => d.stage))].sort((a, b) => order(a) - order(b) || a.localeCompare(b));
  const dayTotal = (day: string) => (byDay.get(day) ?? []).reduce((n, d) => n + d.costMicros, 0);
  const max = niceCeil(Math.max(...days.map(dayTotal)));

  const H = 220;
  const left = 52;
  const bottom = 22;
  const top = 8;
  const plotW = Math.max(width - left, 40);
  const plotH = H - bottom - top;
  const slot = plotW / days.length;
  const barW = Math.max(Math.min(slot * 0.7, 28), 2);
  const y = (micros: number) => top + plotH - (micros / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.max(1, Math.ceil(days.length / Math.max(1, Math.floor(plotW / 64))));

  return (
    <>
      <svg viewBox={`0 0 ${Math.max(width, left + 40)} ${H}`} role="img" aria-label="Cost per day, stacked by stage">
        {ticks.map((t) => (
          <g key={t}>
            <line className="gridline" x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} />
            <text className="axis" x={left - 8} y={y(t) + 4} textAnchor="end">
              {formatSpend(Math.round(t))}
            </text>
          </g>
        ))}
        {days.map((day, i) => {
          const rows = (byDay.get(day) ?? []).slice().sort((a, b) => order(a.stage) - order(b.stage));
          const x = left + i * slot + (slot - barW) / 2;
          let acc = 0;
          const total = dayTotal(day);
          return (
            <g key={day} data-day={day}>
              <title>
                {[`${day} · ${formatSpend(total)}`, ...rows.map((r) => `${r.stage} ${formatSpend(r.costMicros)}`)].join("\n")}
              </title>
              <rect className="hit" x={left + i * slot} y={top} width={slot} height={plotH} />
              {rows
                .filter((r) => r.costMicros > 0)
                .map((r) => {
                  const y1 = y(acc + r.costMicros);
                  const y0 = y(acc);
                  acc += r.costMicros;
                  // A 1px surface gap between stacked segments.
                  const h = Math.max(y0 - y1 - 1, 1);
                  return (
                    <rect key={r.stage} data-stage={r.stage} x={x} y={y1} width={barW} height={h} rx={Math.min(2, barW / 4)} fill={stageColor(r.stage)} />
                  );
                })}
              {i % labelEvery === 0 || i === days.length - 1 ? (
                <text className="axis" x={left + i * slot + slot / 2} y={H - 6} textAnchor="middle">
                  {day.slice(5)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <ul className="spend-legend-list" aria-label="stages">
        {stages.map((s) => (
          <li key={s}>
            <i style={{ background: stageColor(s) }} aria-hidden="true" />
            {s}
          </li>
        ))}
      </ul>
    </>
  );
}

function BreakdownPanel({ range, reload }: { range: SpendRange; reload: number }): JSX.Element {
  const [by, setBy] = useState<SpendDimension>("organisation");
  const read = useRead<{ breakdown: SpendBreakdown }>(`/api/spend/breakdown?by=${by}&${rangeQuery(range)}`, reload);
  const b = read.data?.breakdown ?? null;
  // The rows on screen are the chosen dimension's, never the last one's.
  const rows = b && b.by === by ? b.rows : null;

  let body: JSX.Element;
  if (read.error) body = <Refusal message={read.error} />;
  else if (rows === null) body = <p className="panel-body" aria-live="polite">Loading…</p>;
  else if (rows.length === 0) body = <p className="empty">Nothing to break down in this range.</p>;
  else {
    body = (
      <table className="grid spend">
        <thead>
          <tr>
            <th>{DIMENSION_LABELS[by]}</th>
            <UsageHeads />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key ?? "none"}>
              <td>
                {r.label ?? r.key ?? <span className="dim">none</span>}
                {by === "model" && r.provider ? <span className="sub">{r.provider}</span> : null}
              </td>
              <UsageCells u={r} unpricedHref={by === "model" ? "#prices" : undefined} />
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <section className="panel" aria-label="Breakdown">
      <div className="panel-head wrap">
        <h2>Breakdown</h2>
        <div className="tabs dims" role="tablist" aria-label="Group by">
          {SPEND_DIMENSIONS.map((d) => (
            <button key={d} type="button" role="tab" aria-selected={d === by} onClick={() => setBy(d)}>
              {DIMENSION_LABELS[d]}
            </button>
          ))}
        </div>
      </div>
      <div className="panel-body flush scroll-x">{body}</div>
    </section>
  );
}

function TopRequestsPanel({ top }: { top: SpendTopRequests | null }): JSX.Element {
  let body: JSX.Element;
  if (top === null) body = <p className="panel-body" aria-live="polite">Loading…</p>;
  else if (top.rows.length === 0) body = <p className="empty">No Request spent anything in this range.</p>;
  else {
    body = (
      <table className="grid spend">
        <thead>
          <tr>
            <th>Request</th>
            <UsageHeads />
          </tr>
        </thead>
        <tbody>
          {top.rows.slice(0, TOP_SHOWN).map((r) => (
            <tr key={r.requestId}>
              <td>
                <a href={spendRequestPath(r.requestId)}>{requestName(r.requestTitle)}</a>
                <span className="sub">
                  {r.organisationName ?? "—"} · {r.projectName ?? "—"}
                </span>
              </td>
              <UsageCells u={r} />
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <section className="panel" aria-label="Top Requests">
      <div className="panel-head">
        <h2>Top Requests</h2>
        <span className="meta">most expensive first</span>
      </div>
      <div className="panel-body flush scroll-x">{body}</div>
    </section>
  );
}

function signedSpend(micros: number): string {
  if (micros > 0) return `+${formatSpend(micros)}`;
  if (micros < 0) return `−${formatSpend(-micros)}`;
  return formatSpend(0);
}

function gapPercent(p: number | null): string {
  if (p === null) return "—";
  return `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p).toFixed(1)}%`;
}

const gapClass = (micros: number) => (micros > 0 ? "gap-up" : micros < 0 ? "gap-down" : undefined);

function BillCheckPanel({ range }: { range: SpendRange }): JSX.Element {
  const read = useRead<{ billCheck: BillCheck }>(`/api/spend/bill-check?${rangeQuery(range)}`, 0);
  const bill = read.data?.billCheck ?? null;

  let body: JSX.Element;
  if (read.error) body = <div className="panel-body"><Refusal message={read.error} /></div>;
  else if (bill === null) body = <p className="panel-body" aria-live="polite">Loading…</p>;
  else if (!bill.available) {
    body = (
      <div className="panel-body">
        <div className="notice warn">
          <div>
            <div>{bill.message}</div>
            <div>
              The management key is added or replaced on <a href="/credentials">Credentials</a>.
            </div>
          </div>
        </div>
      </div>
    );
  } else if (bill.rows.length === 0) {
    body = <p className="empty">Neither the ledger nor the bill has a row in this range.</p>;
  } else {
    body = (
      <table className="grid spend">
        <thead>
          <tr>
            <th>Day</th>
            <th>Model</th>
            <th className="num">Ledger</th>
            <th className="num">Billed</th>
            <th className="num">Gap</th>
            <th className="num">Gap %</th>
            <th className="num">Unpriced</th>
          </tr>
        </thead>
        <tbody>
          {bill.rows.map((r) => (
            <tr key={`${r.day} ${r.model}`}>
              <td className="nowrap">{r.day}</td>
              <td className="mono">{r.model}</td>
              <td className="num">{formatSpend(r.ledgerMicros)}</td>
              <td className="num">{formatSpend(r.billedMicros)}</td>
              <td className={`num ${gapClass(r.gapMicros) ?? ""}`}>{signedSpend(r.gapMicros)}</td>
              <td className="num">{gapPercent(r.gapPercent)}</td>
              <td className="num">{r.unpricedReports}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total</td>
            <td className="num">{formatSpend(bill.totals.ledgerMicros)}</td>
            <td className="num">{formatSpend(bill.totals.billedMicros)}</td>
            <td className={`num ${gapClass(bill.totals.gapMicros) ?? ""}`} data-testid="bill-gap">
              {signedSpend(bill.totals.gapMicros)}
            </td>
            <td className="num">{gapPercent(bill.totals.gapPercent)}</td>
            <td className="num">{bill.totals.unpricedReports}</td>
          </tr>
        </tfoot>
      </table>
    );
  }

  return (
    <section className="panel" aria-label="Bill check">
      <div className="panel-head">
        <h2>Bill check</h2>
        <span className="meta">
          the ledger against what OpenRouter billed, per UTC day; a positive gap means the bill is higher
        </span>
      </div>
      <div className="panel-body flush scroll-x">{body}</div>
    </section>
  );
}
